<script lang="ts">
  // libs
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { CartOrderEntity, CartOrderAlertEntity } from "@shared/services/event_tracking/entities";
  import Flash from "@shared/services/flashv2.js";
  import { goto } from "@roxi/routify";
  // components
  import AddingSalesRepHelpModal from "@shared/svelte/Components/AddingSalesRepHelpModal.svelte";
  import AddSalesRepOrAccountNumberAlert from "@retailer/svelte/Cart/Page/DistributorSection/AddSalesRepOrAccountNumberAlert.svelte";
  import BelowOrderMinimumAlert from "@retailer/svelte/Cart/Page/DistributorSection/BelowOrderMinimumAlert.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import ConfirmAccountDetailsAlert from "@retailer/svelte/Cart/Page/DistributorSection/ConfirmAccountDetails/ConfirmAccountDetailsAlert.svelte";
  import DistributorAccountNumberHelpModal from "@shared/svelte/Components/DistributorAccountNumberHelpModal.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import TabcSpiritDeliveryMinimumModal from "@retailer/svelte/Cart/Page/States/Texas/TabcSpiritDeliveryMinimumModal.svelte";
  import OrderBackupNotesModal from "@retailer/svelte/Cart/Page/OrderBackupNotesModal.svelte";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";

  // types
  import type { CartOrderType } from "@shared/types/Cart";
  import type { CartProduct } from "@shared/types/ProductLine";

  // props
  export let cartService: any;
  export let highlightErrors: boolean;
  export let openConnectDistributorModal: any;
  export let order: CartOrderType;

  // data
  let showAddingSalesRepHelpModal: boolean = false;
  let showDistributorAccountNumberHelpModal: boolean = false;
  let showTabcDeliveryMinimumModal: boolean = false;
  let showNoteModal: boolean = false;
  let unorderableOutOfStockProducts: boolean = false;
  let unorderableNotSoldAtThisLocationProducts: boolean = false;

  const { trackMrcEvents, trackClick } = EventTracking;

  // auth service
  let authService = getControlStateAuthenticationService();

  // functions
  const needsReps = order => {
    if (needsAccountNumber(order)) {
      return false;
    }

    return (
      !order.distributor.reasons?.rep ||
      (!order.distributor.reasons?.no_rep_integration && !order.sales_reps?.length)
    );
  };

  const needsAccountNumber = order => {
    return !order.distributor.reasons?.account;
  };

  const retailerUserAuthStatus = order => {
    return order?.distributor?.reasons?.retailer_user_authentication_status;
  };

  const needsRetailerUserAuth = order => {
    let status = retailerUserAuthStatus(order);
    return status == "pending" || status == "invalid";
  };

  function isUnavailable(product: CartProduct, order: any) {
    let productOtherwiseUnavailable = product.is_available == false && !isAllocated(product);
    return (
      productOtherwiseUnavailable || isHidden(product) || order.distributor.associated == false
    );
  }

  const isOrderable = (product: CartProduct) => {
    if (isLimitedAllocation(product)) {
      return false;
    } else if (product.orderable !== undefined) {
      return product.orderable;
    } else {
      return true;
    }
  };

  function isAllocated(product: CartProduct) {
    return (product.allocation && product.allocation.is_allocated) || product.orderable == false;
  }

  function isHidden(product: CartProduct) {
    return product.inventory && product.inventory.hidden;
  }

  const saveNote = async () => {
    let backupNotes = order.backup_notes;
    let orderNotes = order.retailer_notes;

    await cartService.saveNote(order.id, { orderNotes, backupNotes }).catch(err => {
      if (err.status === 404) {
        Flash.now({
          type: "error",
          header: "Order was not found",
          message: "All items were removed from your order",
        });
      }
    });

    cartService.load();
    showNoteModal = false;
  };

  const salesRepNames = () => order.sales_reps?.map(rep => rep.name);

  const isLimitedAllocation = (product: CartProduct) =>
    product.allocation?.is_allocated && product.allocation?.amount == 0;

  // reactives
  $: orderProblems = function () {
    if (!order) {
      return false;
    }
    if (window["$rootScope"]?.user?.role === "sales_rep") {
      return false;
    }
    return (
      order.products?.some(p => isUnavailable(p, order)) ||
      order.products?.some(p => !isOrderable(p)) ||
      unorderableOutOfStockProducts ||
      unorderableNotSoldAtThisLocationProducts ||
      order.distributor.associated == false
    );
  };
  $: unorderableOutOfStockProducts =
    cartService.orderHasProductsOutOfStock(order) &&
    !order?.distributor?.allow_ordering_out_of_stock;
  $: unorderableNotSoldAtThisLocationProducts =
    cartService.orderHasProductsNotSoldAtThisLocation(order);
  $: isBelowTabcSpiritDeliveryMinimum = order?.tabc_spirit_delivery_remaining > 0;
  $: doesNotMeetMinimumOrderQuantity = order?.minimum_order_quantity_reached == false;
  $: isBlockedOnCustomOrderBlockingFlag = cartService.orderBlockedOnCustomOrderBlockingFlag(order);
  $: hasOnlyRequestableItems = cartService.orderHasOnlyRequestedItems(order);
  $: speakeasyError = order?.distributor.speakeasy_error;
  $: productNotFoundProducts = cartService.orderHasProductNotFoundProducts(order);
</script>

<FizzStyles>
  <div data-testid="order-alerts">
    <AddingSalesRepHelpModal bind:showAddingSalesRepHelpModal />
    <DistributorAccountNumberHelpModal bind:showModal={showDistributorAccountNumberHelpModal} />
    <TabcSpiritDeliveryMinimumModal
      bind:show={showTabcDeliveryMinimumModal}
      distributor={order.distributor.name}
      volume={order.tabc_spirit_delivery_remaining}
    />
    {#if needsRetailerUserAuth(order) && authService?.cartCtaComponent}
      <svelte:component
        this={authService?.cartCtaComponent}
        {authService}
        authStatus={retailerUserAuthStatus(order)}
        {highlightErrors}
      />
    {/if}
    {#if needsAccountNumber(order) && !needsRetailerUserAuth(order)}
      <AddSalesRepOrAccountNumberAlert
        {openConnectDistributorModal}
        {order}
        {highlightErrors}
        alertText={"account number"}
      />
    {/if}
    {#if needsReps(order) && !needsRetailerUserAuth(order)}
      <AddSalesRepOrAccountNumberAlert
        {openConnectDistributorModal}
        {order}
        {highlightErrors}
        alertText={"sales rep"}
      />
    {/if}
    {#if isBelowTabcSpiritDeliveryMinimum}
      <div
        class="fizz-stack-12 fizz-alert--small"
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        id="tabc-spirit-delivery-requirement-{order.distributor.id}"
        use:trackMrcEvents={[
          [
            new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
            new CartOrderAlertEntity({ alert_type: "tabc_minimum" }),
          ],
        ]}
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Order below minimum:</span>
          TABC requires 2.4 gal volume minimum for delivery.
        </p>
      </div>
    {/if}
    {#if doesNotMeetMinimumOrderQuantity}
      <div
        class="fizz-stack-12 fizz-alert--small"
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        id="minimum-order-quantity-requirement-{order.distributor.id}"
        use:trackMrcEvents={[
          [
            new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
            new CartOrderAlertEntity({ alert_type: "minimum_order_quantity" }),
          ],
        ]}
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Items below order minimum:</span>
          Items in your order are below their required order minimums. Add additional quantities to order.
        </p>
      </div>
    {/if}
    {#if isBlockedOnCustomOrderBlockingFlag}
      <div
        class="fizz-stack-12 fizz-alert--small"
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        data-testid="{order.distributor.name}-custom-block"
        use:trackMrcEvents={[
          [
            new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
            new CartOrderAlertEntity({ alert_type: "order_blocking" }),
          ],
        ]}
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Account error:</span>
          There is an issue with your account at {order.distributor.name} and we are not able to send
          your order at this time. Please contact your sales rep at {order.distributor.name}
          to resolve the issue.
        </p>
      </div>
    {/if}
    {#if hasOnlyRequestableItems}
      <div
        class="fizz-stack-12 fizz-alert--small"
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        data-testid="{order.distributor.name}-requestable-items"
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Add more products:</span>
          Due to the risk of fulfillment, requests can only be sent as part of an order.
        </p>
        <div class="fizz--alert-links">
          <button
            class="fizz-button-small"
            on:click={() => {
              trackClick("request_item_alert_shop_distributor_button", [
                new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
                new CartOrderAlertEntity({ alert_type: "only_requestable_items" }),
              ]);
              $goto(`/product_listing?distributors=${order.distributor.id}`);
            }}>Shop Distributor</button
          >
        </div>
      </div>
    {/if}
    {#if order.distributor.distributor_account_id}
      <ConfirmAccountDetailsAlert
        requiresMatchConfirmation={order.distributor.reasons?.requires_match_confirmation}
        {highlightErrors}
        distributorId={order.distributor.id}
        salesRepNames={salesRepNames()}
        reloadCart={() => cartService.load()}
        orderId={order.id}
      />
    {/if}

    {#if productNotFoundProducts}
      <div class="fizz-stack-12 fizz-alert--small fizz-alert-warning">
        <p class="fizz-text-small">
          Items in your cart may not be in stock. We haven't received inventory information for some
          products.
        </p>
      </div>
    {/if}

    {#if orderProblems() && !speakeasyError}
      <div
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        class="fizz-stack-12 fizz-alert--small"
        use:trackMrcEvents={[
          [
            new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
            new CartOrderAlertEntity({ alert_type: "stock_changes" }),
          ],
        ]}
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Product stock changes:</span>
          Items in your order are not currently available. Review the items in your cart.
        </p>
      </div>
    {/if}

    {#if speakeasyError}
      <div
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        class="fizz-stack-12 fizz-alert--small"
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Cart error:</span>
          Unable to submit your order at this time. Please contact support for assistance.
        </p>
      </div>
    {/if}

    {#if $CurrentRetailerStore?.disable_ordering_for_non_partner_distributor && !order.distributor.texas_partner}
      <div
        class:fizz-alert-warning={!highlightErrors}
        class:fizz-alert-error={highlightErrors}
        class="fizz-stack-12 fizz-alert--small"
        use:trackMrcEvents={[
          [
            new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
            new CartOrderAlertEntity({ alert_type: "tx_block_order" }),
          ],
        ]}
      >
        <p class="fizz-text-small">
          <span class="fizz-text-strong">Subscription required:</span>
          Provi requires a $60 annual subscription fee to send orders to non-partner distributors within
          Texas. To learn more about how to opt-in or check the status of your subscription visit the
          Help Center.
        </p>
        <div class="fizz--alert-links">
          <button
            class="fizz-button-small"
            on:click={() => {
              window.open("https://support.provi.com/s/article/Texas-Subscription-FAQs", "_blank");
            }}>Help Center</button
          >
        </div>
      </div>
    {/if}

    <BelowOrderMinimumAlert {highlightErrors} {order} />
  </div>
</FizzStyles>

{#if showNoteModal}
  <OrderBackupNotesModal bind:show={showNoteModal} {order} {saveNote} />
{/if}
